import Box from '@mui/material/Box';
import type {Breakpoint, CSSObject, SxProps, Theme} from '@mui/material/styles';
import {styled} from '@mui/material/styles';
import {mergeClasses, varAlpha} from 'minimal-shared/utils';
import {ComponentProps, ReactNode} from 'react';

import {Logo} from '@minimal/components/logo';
import {NavSectionVertical} from '@minimal/components/nav-section';
import {Scrollbar} from '@minimal/components/scrollbar';
import {NavAppEnv} from '@minimal/layouts/components/nav-app-env';
import {WorkspacesPopover} from '@minimal/layouts/components/workspaces-popover';

import {layoutClasses} from '../core/classes';

// ----------------------------------------------------------------------

export type NavVerticalProps = ComponentProps<'div'> & {
  sx?: SxProps<Theme>;
  cssVars?: CSSObject;
  layoutQuery?: Breakpoint;
  slots?: {
    topArea?: ReactNode;
    bottomArea?: ReactNode;
  };
};

export function NavVertical({sx, slots, cssVars, className, layoutQuery = 'md', ...other}: NavVerticalProps) {
  const renderNavVertical = () => (
    <>
      {slots?.topArea ?? (
        <Box sx={{px: 3.5, pt: 2.5, pb: 1, textAlign: 'center'}}>
          <Logo isSingle={false} />
        </Box>
      )}

      <NavAppEnv sx={{mx: 2, mt: 2.5, mb: 1}} />

      <Box sx={{px: 3.5, pt: 2.5, pb: 1}}>
        <WorkspacesPopover />
      </Box>

      <Scrollbar fillContent>
        <NavSectionVertical cssVars={cssVars} sx={{px: 2, flex: '1 1 auto'}} />

        {slots?.bottomArea}
      </Scrollbar>
    </>
  );

  return (
    <NavRoot
      layoutQuery={layoutQuery}
      className={mergeClasses([layoutClasses.nav.root, layoutClasses.nav.vertical, className])}
      sx={sx}
      {...other}
    >
      {renderNavVertical()}
    </NavRoot>
  );
}

// ----------------------------------------------------------------------

const NavRoot = styled('div', {
  shouldForwardProp: (prop: string) => !['layoutQuery', 'sx'].includes(prop),
})<Pick<NavVerticalProps, 'layoutQuery'>>(({layoutQuery = 'md', theme}) => ({
  top: 0,
  left: 0,
  height: '100%',
  display: 'none',
  position: 'fixed',
  flexDirection: 'column',
  zIndex: 'var(--layout-nav-zIndex)',
  backgroundColor: 'var(--layout-nav-bg)',
  width: 'var(--layout-nav-vertical-width)',
  borderRight: `1px solid var(--layout-nav-border-color, ${varAlpha(theme.vars.palette.grey['500Channel'], 0.12)})`,
  transition: theme.transitions.create(['width'], {
    easing: 'var(--layout-transition-easing)',
    duration: 'var(--layout-transition-duration)',
  }),
  [theme.breakpoints.up(layoutQuery)]: {display: 'flex'},
}));
